import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import useMeasure from 'react-use-measure';
import styled from 'styled-components';
import * as R from 'ramda';
import { Modal } from 'antd';
import dayjs from 'dayjs';

import { blue } from '@ant-design/colors';
import { PoolElement } from './Pool';
import { positions } from './positions';
import { appName, isHandball } from './sportConfig';
import { Play, Snapshot } from './types';
import { useDeletePlayMutation } from './mutations';
import { DrawingMode } from './DrawingCanvas';
import { PlayBrowser } from './components/PlayBrowser';
import { Menu, ViewType } from './components/Menu';
import { useAuthContext } from './AuthProvider';
import html2canvas from 'html2canvas';
import { Toolbar, ToolbarControl } from './components/Toolbar';
import {
  black,
  lighterBlueBackground,
  red,
  handballLightSurround,
  handballLightSurroundEdge,
  handballDarkSurround,
  handballDarkSurroundEdge,
} from './components/colors';
import { Editor } from './components/Editor';
import { EditorContent } from './components/CapNumberingEditor';
import { VersionInfo } from './components/VersionInfo';
import { ProfilePage } from './components/ProfilePage';
import { AdminContainer } from './AdminContainer';
import { useStore } from '@nanostores/react';
import { $downloadEvent } from './store/download';
import { isIOS, isMobileSafari } from 'react-device-detect';
import { UpgradeSubscription } from './purchase/UpgradeSubscription';
import { Button } from './ui/Button';
import { $isHalfView } from './store/editor';
import { $plays, $playsIsLoading, getPlays, savePlay } from './store/plays';
import { $animationMode, $isOnline, TransitionMode } from './store/app';
import { v4 as uuidv4 } from 'uuid';

import {
  $activeSnapshot,
  $activeSnapshotIndex,
  $namings,
  $tactic,
  $tacticSnapShots,
  updateTacticNamings,
  updateTacticSnapshot,
} from './store/tactic';
import { SwitchTransitionModeButton } from './components/plays/SwitchTransitionModeButton';
import { SwitchMovementModeButton } from './components/plays/SwitchMovementModeButton';
import { $movementMode, MovementMode } from './store/app';
import {
  FPS,
  TransitionPaths,
  calculateObjectFrameCounts,
  calculateTransitionFrameCount,
  computeInterpolatedSnapshot,
  generateTransitionDiffSnapshot,
  prepareTransitionPaths,
} from './utils/animation';

const OPPONENT_INCREASE = 100;

// Transition configuration (the interpolation math itself lives in
// utils/animation.ts, shared with usePlaybackEngine/PlayPlayer)
let TRANSITION_MODE = $animationMode.get();

const TacticBoardContainer = styled.div<{ $colorMode: 'light' | 'dark' }>`
  width: 100%;
  height: 100%;
  display: flex;
  background-image: ${({ $colorMode }) => {
    // Handball uses its own court-surround gradients in both modes (green
    // identity); waterpolo keeps the original blue/dark gradients.
    if ($colorMode === 'light') {
      return isHandball
        ? `radial-gradient(${handballLightSurround} 0%, ${handballLightSurroundEdge} 100%)`
        : `radial-gradient(${blue[2]} 0%, ${blue[5]} 100%)`;
    }
    return isHandball
      ? `radial-gradient(${handballDarkSurround} 0%, ${handballDarkSurroundEdge} 100%)`
      : `radial-gradient(${lighterBlueBackground} 0%, ${black} 100%)`;
  }};
`;

const MainView = styled.div<{ $colorMode: 'light' | 'dark' }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MainContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const PlayViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  width: 100%;
  height: 100%;
  /* border: 1px solid green; */
`;
const EditorContainer = styled.div<{ $height: number }>`
  display: flex;
  flex-grow: 0;
  flex-shrink: 1;
  width: 100%;
  justify-content: center;
  align-items: center;
  height: ${(props) => props.$height}px;
  /* border: 1px solid red; */
`;

const PoolContainer = styled.div`
  display: flex;
  flex-grow: 1;
  width: 100%;
  align-items: center;
  justify-content: center;
  /* border: 1px solid yellow; */
`;

const MainViewRatio: Record<EditorContent, number> = {
  // Cap numbering is now a floating modal (CapNumberingEditor) instead of an
  // inline button strip, so it reserves no editor height — the pool keeps its
  // full size and the modal overlays it.
  [EditorContent.CAP_NUMBERING]: 0,
  [EditorContent.PLAY_EDITING]: 0.3,
};

export const TacticBoard = () => {
  // // Whether we are running as an installed PWA or not.
  // const isInstalledPWA =
  //   window.matchMedia("(display-mode: window-controls-overlay)").matches ||
  //   window.matchMedia("(display-mode: standalone)").matches
  const downloadEvent = useStore($downloadEvent);
  const [activeView, setActiveView] = useState<ViewType>(ViewType.PLAY);
  const [previousView, setPreviousView] = useState<ViewType>(ViewType.PLAY);
  const [activeToolbarControl, setActiveToolbarControl] = useState<
    ToolbarControl | undefined
  >();

  const [activeEditor, setActiveEditor] = useState<EditorContent | undefined>();

  const resetToolbar = useCallback(() => {
    setActiveToolbarControl(undefined);
  }, []);

  const currentFormation = useStore($tactic);
  const snapShots = useStore($tacticSnapShots);
  const namings = useStore($namings);
  const activeSnapshotIndex = useStore($activeSnapshotIndex);
  const currentSnapshot = useStore($activeSnapshot);
  const animationMode = useStore($animationMode);
  const movementMode = useStore($movementMode);

  const [halfView, setHalfView] = useState<boolean>(true);
  const [shouldCleanDrawings, setShouldCleanDrawings] =
    useState<boolean>(false);
  const [shouldUndoLastDrawing, setShouldUndoLastDrawing] =
    useState<boolean>(false);
  const [drawingMode, setDrawingMode] = useState<DrawingMode>(DrawingMode.NONE);

  const [isDeletingPlayModalOpen, setDeletingPlayModalOpen] = useState(false);
  const [selectedPlayForDelete, setSelectedPlayForDelete] =
    useState<Play | null>(null);

  const [deleteInProgress, setDeleteInProgress] = useState<boolean>(false);
  const [isEditorOpen, setIsEditorOpen] = useState<boolean>(true);

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  // Mirror isPlaying into a ref so the setTimeout-driven transition ticks read
  // the LIVE value, not the value captured in the closure when the timer was
  // scheduled. On iPad the heavy per-tick Konva redraw saturates the main
  // thread and delays the setIsPlaying(false) commit past the next ~33ms tick,
  // so a stale closure kept auto-advancing after pause. The ref is set
  // synchronously in startPlaying/pausePlaying (the race-critical paths) and
  // kept in sync here for every other setIsPlaying call site.
  const isPlayingRef = useRef<boolean>(false);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  const [isPlayingFinished, setIsPlayingFinished] = useState<boolean>(false);

  const playerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const goToNextFrameRef = useRef<(() => void) | null>(null);

  const [mainViewRef, { width, height }] = useMeasure();
  const poolRef = useRef<HTMLElement | null>(null);

  const transitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isTransitioning, setIsTransitoning] = useState<boolean>(false);
  const [currentTransitionPositition, setCurrentTransitionPosition] =
    useState<number>(0);
  const [currentTransitionFrameCount, setCurrentTransitionFrameCount] =
    useState<number>(0);
  const [currentTransitionDiffSnapshot, setCurrentTransitionDiffSnapshot] =
    useState<Snapshot | null>();

  // Store individual frame counts for each object (for DISTANCE_BASED mode)
  const [transitionObjectFrameCounts, setTransitionObjectFrameCounts] =
    useState<{
      ball: number;
      goalkeepers: number[];
      players: number[];
      opponents: number[];
    } | null>(null);

  // Store start and end snapshots for interpolation
  const [transitionStartSnapshot, setTransitionStartSnapshot] =
    useState<Snapshot | null>(null);
  const [transitionEndSnapshot, setTransitionEndSnapshot] =
    useState<Snapshot | null>(null);

  // Prepared recorded paths for the running transition (null = all linear)
  const [transitionPaths, setTransitionPaths] =
    useState<TransitionPaths | null>(null);

  const [originSnapshotPosition, setOriginSnapshotPosition] =
    useState<number>(0);

  useEffect(() => {
    TRANSITION_MODE = animationMode;
  }, [animationMode]);

  const [colorMode, setColorMode] = useLocalStorageState<'light' | 'dark'>(
    'colorMode',
    { defaultValue: 'dark' },
  );

  const [selectedPlayer, setSelectedPlayer] = useState<number | null>(null);

  const currentSnapshotCount = useMemo(() => {
    return currentFormation?.snapshots?.length || 0;
  }, [snapShots]);

  // Cap numbers already used by OTHER players on the same team as the player
  // being numbered. namings indexes own players below OPPONENT_INCREASE and
  // opponents at/above it; goalkeepers are not numbered. Passed to the picker
  // so duplicates within a team are highlighted.
  const usedCapNumbers = useMemo(() => {
    if (selectedPlayer === null) return [];
    const selectedIsOpponent = selectedPlayer >= OPPONENT_INCREASE;
    const result: number[] = [];
    namings.forEach((value, index) => {
      if (index === selectedPlayer || value == null || value === '') return;
      if (index >= OPPONENT_INCREASE !== selectedIsOpponent) return;
      const parsed = parseInt(value, 10);
      if (!Number.isNaN(parsed)) result.push(parsed);
    });
    return result;
  }, [namings, selectedPlayer]);

  const isOnline = useStore($isOnline);
  const playsStore = useStore($plays);
  const playsIsLoading = useStore($playsIsLoading);
  const [isStandalone, setIsStandalone] = useState<boolean>(false);

  useEffect(() => {
    const _isInStandaloneMode =
      'standalone' in window.navigator
        ? window.navigator.standalone
        : window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(!!_isInStandaloneMode);
  }, []);

  const { isPro, isAdmin, data: { licenseGroup } = {} } = useAuthContext();

  const loadPlays = useCallback(async () => {
    if (!isPro) {
      return;
    }
    await getPlays();
  }, [isPro]);

  useEffect(() => {
    const _loadPlays = () => {
      void loadPlays();
    };
    void _loadPlays();
  }, []);

  useEffect(() => {
    if (activeToolbarControl === ToolbarControl.NUMBERING) {
      setActiveEditor(EditorContent.CAP_NUMBERING);
      setIsEditorOpen(true);
      setShouldCleanDrawings(true); // clear drawings when entering numbering mode
      return;
    }
    if (activeToolbarControl === ToolbarControl.EDITING) {
      setActiveEditor(EditorContent.PLAY_EDITING);
      setIsEditorOpen(true);
      setShouldCleanDrawings(true); // clear drawings when entering editing mode
      return;
    }
    setIsEditorOpen(false);
    setActiveEditor(undefined);
  }, [activeToolbarControl]);

  useEffect(() => {
    if (activeToolbarControl !== ToolbarControl.NUMBERING) {
      setSelectedPlayer(null);
      return;
    }
  }, [activeToolbarControl]);

  const { mutateAsync: deletePlay, isLoading: isDeletingPlay } =
    useDeletePlayMutation();

  const plays = useMemo(() => {
    // console.log('playsStore::changed', playsStore)
    return [...playsStore, ...R.clone(positions)];
  }, [playsStore, isOnline]);

  const playNameList = useMemo(() => plays.map((play) => play.name), [plays]);

  // Formation folders shown in the play browser. Handball adds its 7-6 (empty
  // goal) formation before Custom; waterpolo has no 7-6 so it is handball-only.
  const folders = isPro
    ? isHandball
      ? ['6 - 6', '6 - 5', '7 - 6', 'Custom']
      : ['6 - 6', '6 - 5', 'Custom']
    : [];

  // useEffect(() => {
  //   const currentFormation = plays.find(({ id }) => id === currentFormationId)
  //   setCurrentFormation(currentFormation ? { ...currentFormation } : undefined)
  //   setactiveSnapshotIndex(0)
  //
  //   setNamings(currentFormation?.namings || Array(6).fill(null))
  //
  //   return () => {
  //     playerTimer.current && clearInterval(playerTimer.current)
  //   }
  // }, [currentFormationId, plays])

  // ----- Player logic
  useEffect(() => {
    if (isTransitioning) {
      transitionTimer.current = setTimeout(() => {
        const nextTransitionPosition = currentTransitionPositition + 1;
        if (nextTransitionPosition > currentTransitionFrameCount) {
          setIsTransitoning(false);
          setCurrentTransitionDiffSnapshot(null);
          setCurrentTransitionFrameCount(0);
          setTransitionObjectFrameCounts(null);
          setTransitionStartSnapshot(null);
          setTransitionEndSnapshot(null);
          setTransitionPaths(null);
          // Read the LIVE playing flag (not the closure) so a pause that landed
          // during this transition reliably stops auto-advance on every device.
          if (isPlayingRef.current) {
            goToNextFrameRef.current?.();
          }
        } else {
          setCurrentTransitionPosition(nextTransitionPosition);
        }
      }, 1000 / FPS);
    } else {
      transitionTimer.current && clearTimeout(transitionTimer.current);
    }

    return () => {
      if (transitionTimer.current) {
        clearTimeout(transitionTimer.current);
        transitionTimer.current = null;
      }
    };
  }, [
    isTransitioning,
    currentTransitionPositition,
    currentTransitionFrameCount,
  ]);

  const getCurrentSnapshot = useCallback(() => {
    if (!currentFormation) {
      return undefined;
    }
    const currentSnapshotCount = currentFormation?.snapshots?.length || 0;
    if (isTransitioning && currentTransitionDiffSnapshot) {
      const originSnapshot =
        currentFormation!.snapshots[originSnapshotPosition];
      const targetSnapshot = currentFormation!.snapshots[activeSnapshotIndex];

      return computeInterpolatedSnapshot(
        originSnapshot!,
        transitionStartSnapshot || originSnapshot!,
        transitionEndSnapshot || targetSnapshot!,
        currentTransitionDiffSnapshot,
        currentTransitionPositition,
        TRANSITION_MODE,
        transitionObjectFrameCounts,
        transitionPaths,
        currentTransitionFrameCount,
      );
    }
    const currentSnapshot =
      currentFormation?.snapshots[
        activeSnapshotIndex >= currentSnapshotCount - 1
          ? currentSnapshotCount - 1
          : activeSnapshotIndex
      ];
    return currentSnapshot ?? undefined;
  }, [
    currentFormation,
    activeSnapshotIndex,
    isTransitioning,
    currentTransitionDiffSnapshot,
    originSnapshotPosition,
    currentTransitionPositition,
    currentTransitionFrameCount,
    transitionObjectFrameCounts,
    transitionStartSnapshot,
    transitionEndSnapshot,
    transitionPaths,
  ]);

  // const getCurrentSnapshot = (): Snapshot | undefined => {
  //   if (!currentFormation) {
  //     return undefined
  //   }
  //   const currentSnapshotCount = currentFormation?.snapshots?.length || 0
  //   if (isTransitioning && currentTransitionDiffSnapshot) {
  //     const originSnapshot = currentFormation!.snapshots[originSnapshotPosition]
  //     return {
  //       ball: getScaledSumVector(
  //         originSnapshot!.ball,
  //         currentTransitionDiffSnapshot.ball,
  //         currentTransitionPositition
  //       ),
  //       goalkeepers: originSnapshot!.goalkeepers.map((endPos, index) =>
  //         getScaledSumVector(
  //           (endPos as unknown) as Position,
  //           (currentTransitionDiffSnapshot.goalkeepers[
  //             index
  //           ] as unknown) as Position,
  //           currentTransitionPositition
  //         )
  //       ),
  //       players: originSnapshot!.players.map((endPos, index) =>
  //         getScaledSumVector(
  //           (endPos as unknown) as Position,
  //           (currentTransitionDiffSnapshot.players[
  //             index
  //           ] as unknown) as Position,
  //           currentTransitionPositition
  //         )
  //       ),
  //       opponents: originSnapshot!.opponents.map((endPos, index) =>
  //         getScaledSumVector(
  //           (endPos as unknown) as Position,
  //           (currentTransitionDiffSnapshot.opponents[
  //             index
  //           ] as unknown) as Position,
  //           currentTransitionPositition
  //         )
  //       )
  //     }
  //   }
  //   const currentSnapshot =
  //     currentFormation?.snapshots[
  //       activeSnapshotIndex >= currentSnapshotCount - 1
  //         ? currentSnapshotCount - 1
  //         : activeSnapshotIndex
  //     ]
  //   return currentSnapshot ?? undefined
  // }

  const startReplaying = useCallback(() => {
    $activeSnapshotIndex.set(0);
    setIsPlayingFinished(false);
  }, []);

  const skipTransition = useCallback(() => {
    if (isTransitioning) {
      // Clear the transition timer
      if (transitionTimer.current) {
        clearTimeout(transitionTimer.current);
        transitionTimer.current = null;
      }
      // Immediately end transition
      setIsTransitoning(false);
      setCurrentTransitionPosition(0);
      setCurrentTransitionDiffSnapshot(null);
      setCurrentTransitionFrameCount(0);
      setTransitionObjectFrameCounts(null);
      setTransitionStartSnapshot(null);
      setTransitionEndSnapshot(null);
      setTransitionPaths(null);
    }
  }, [isTransitioning]);

  const goToNextFrame = useCallback(() => {
    // Skip any running transition first
    skipTransition();

    const nextSnapshotPosition =
      activeSnapshotIndex + 1 === currentSnapshotCount
        ? 0
        : activeSnapshotIndex + 1;
    if (nextSnapshotPosition === 0) {
      if (isPlaying) {
        setIsPlayingFinished(true);
      }
      setIsPlaying(false);
      return;
    }

    const startSnapshot = currentFormation!.snapshots[activeSnapshotIndex]!;
    const endSnapshot = currentFormation!.snapshots[nextSnapshotPosition]!;

    // Recorded paths for a forward transition live on the target snapshot
    const paths = prepareTransitionPaths(
      startSnapshot,
      endSnapshot,
      endSnapshot.paths,
      false,
    );

    // Calculate dynamic frame count based on mode
    const frameCount = calculateTransitionFrameCount(
      startSnapshot,
      endSnapshot,
      TRANSITION_MODE,
      paths,
    );

    // For DISTANCE_BASED mode, calculate individual frame counts for each object
    if (TRANSITION_MODE === TransitionMode.DISTANCE_BASED) {
      setTransitionObjectFrameCounts(
        calculateObjectFrameCounts(startSnapshot, endSnapshot, paths),
      );
    }
    setTransitionStartSnapshot(startSnapshot);
    setTransitionEndSnapshot(endSnapshot);
    setTransitionPaths(paths);

    setIsTransitoning(true);
    setCurrentTransitionPosition(0);
    setCurrentTransitionFrameCount(frameCount);
    setCurrentTransitionDiffSnapshot(
      generateTransitionDiffSnapshot(startSnapshot, endSnapshot, frameCount),
    );
    setOriginSnapshotPosition(activeSnapshotIndex);
    $activeSnapshotIndex.set(nextSnapshotPosition);
  }, [
    activeSnapshotIndex,
    currentSnapshotCount,
    isPlaying,
    currentFormation,
    skipTransition,
  ]);

  // Update the ref whenever goToNextFrame changes
  useEffect(() => {
    goToNextFrameRef.current = goToNextFrame;
  }, [goToNextFrame]);

  const goToPrevFrame = useCallback(() => {
    // Skip any running transition first
    skipTransition();

    const prevSnapshotPosition =
      activeSnapshotIndex === 0
        ? currentSnapshotCount - 1
        : activeSnapshotIndex - 1;
    if (prevSnapshotPosition === currentSnapshotCount - 1) {
      setIsPlaying(false);
      return;
    }

    const startSnapshot = currentFormation!.snapshots[activeSnapshotIndex]!;
    const endSnapshot = currentFormation!.snapshots[prevSnapshotPosition]!;

    // Stepping backward undoes the (prev -> current) transition, whose
    // recorded paths live on the CURRENT (start) snapshot and run in reverse
    const paths = prepareTransitionPaths(
      startSnapshot,
      endSnapshot,
      startSnapshot.paths,
      true,
    );

    // Calculate dynamic frame count based on mode
    const frameCount = calculateTransitionFrameCount(
      startSnapshot,
      endSnapshot,
      TRANSITION_MODE,
      paths,
    );

    // For DISTANCE_BASED mode, calculate individual frame counts for each object
    if (TRANSITION_MODE === TransitionMode.DISTANCE_BASED) {
      setTransitionObjectFrameCounts(
        calculateObjectFrameCounts(startSnapshot, endSnapshot, paths),
      );
    }
    setTransitionStartSnapshot(startSnapshot);
    setTransitionEndSnapshot(endSnapshot);
    setTransitionPaths(paths);

    setIsTransitoning(true);
    setCurrentTransitionPosition(0);
    setCurrentTransitionFrameCount(frameCount);
    setCurrentTransitionDiffSnapshot(
      generateTransitionDiffSnapshot(startSnapshot, endSnapshot, frameCount),
    );
    setOriginSnapshotPosition(activeSnapshotIndex);
    $activeSnapshotIndex.set(prevSnapshotPosition);
  }, [
    activeSnapshotIndex,
    currentSnapshotCount,
    currentFormation,
    skipTransition,
  ]);

  const startPlaying = useCallback(() => {
    isPlayingRef.current = true;
    setIsPlaying(true);
    setIsPlayingFinished(false);
    goToNextFrame();
  }, [goToNextFrame]);

  const pausePlaying = useCallback(() => {
    // Stop authoritatively: flip the live ref synchronously so any transition
    // tick already queued for this frame will NOT auto-advance, regardless of
    // when React commits the state update. Previously this relied solely on
    // setIsPlaying(false) landing before the next ~33ms tick — a race iPad lost,
    // so playback kept advancing. The old early-return on the last leg also made
    // pause a no-op there (e.g. 2-snapshot plays); it is removed so pause always
    // stops. The finished state is set naturally at the end of playback
    // (goToNextFrame wrap branch), not on pause.
    isPlayingRef.current = false;
    setIsPlaying(false);
  }, []);

  const onSelectFrameManually = useCallback(
    (frameIndex: number) => {
      // Skip any running transition first
      skipTransition();
      // Stop playing if it was running
      setIsPlaying(false);
      // Jump to the selected frame
      $activeSnapshotIndex.set(frameIndex);
    },
    [skipTransition],
  );

  useEffect(() => {
    if (isPlayingFinished && activeSnapshotIndex === 0) {
      // setTimeout(() => {
      //   startPlaying()
      // }, 2000)
    }
  }, [isPlaying, activeSnapshotIndex, isPlayingFinished, startPlaying]);

  const onRemoveFrame = useCallback(() => {
    if (currentSnapshotCount > 1) {
      const snapshotsCopy = [...(currentFormation!.snapshots || [])];
      snapshotsCopy.splice(activeSnapshotIndex, 1);
      $tactic.setKey('snapshots', snapshotsCopy);
      $activeSnapshotIndex.set(
        activeSnapshotIndex === 0 ? 0 : activeSnapshotIndex - 1,
      );
    }
  }, [currentSnapshotCount, currentFormation, activeSnapshotIndex]);

  const onAddNewFrame = useCallback(() => {
    // TODO insert frame to next to current
    const snapshotsCopy = [...(currentFormation!.snapshots || [])];
    if (currentSnapshot) {
      // Fresh copy without paths: the new frame starts with no recorded
      // movement, and pushing the same object reference would otherwise
      // share (and later corrupt) state between frames.
      snapshotsCopy.push({ ...(currentSnapshot as Snapshot), paths: null });
    }
    $tactic.setKey('snapshots', snapshotsCopy);
    $activeSnapshotIndex.set(activeSnapshotIndex + 1);
    // setactiveSnapshotIndex((currentFormation?.snapshots?.length || 1) - 1)
  }, [currentFormation, currentSnapshot, activeSnapshotIndex]);

  // const onFormationChange = (id: string) => {
  //   setCurrentFormationId(id)
  //   setShouldCleanDrawings(true)
  // }

  const onFormationChange = useCallback(
    (playId: string) => {
      const play = plays.find((play) => play.id === playId);
      if (!play) {
        return;
      }
      $tactic.set({
        ...play,
        name: play.name + (play.category === 'Custom' ? '' : ' DRAFT'),
        isDraft: play.category !== 'Custom',
        synced: false,
      });
      playerTimer.current && clearInterval(playerTimer.current);
      $activeSnapshotIndex.set(0);
      setShouldCleanDrawings(true);
      // setCurrentFormation(currentFormation ? { ...currentFormation } : undefined)
      // setactiveSnapshotIndex(0)
    },
    [plays],
  );

  // ----- Saving play logic

  // This is used to handle the after effects for a play submission.
  // It is currently commented out because it was causing issues with the
  // play submission flow, but it can be uncommented if needed.
  // It was intended to reset the toolbar, refetch formations, and set the current formation ID
  // useEffect(() => {
  //   const effectFunc = async () => {
  //     if (isSubmitPlaySuccess) {
  //       resetToolbar()
  //       // setCurrentFormationId(submittedPlay!.id!)
  //       resetSubmitPlay()
  //     }
  //   }
  //   effectFunc()
  // }, [isSubmitPlaySuccess, resetToolbar, resetSubmitPlay, submittedPlay])

  const onSubmitPlay = ({
    saveAsNew,
    name,
  }: {
    saveAsNew: boolean;
    name?: string;
  }) => {
    const id = uuidv4();
    $tactic.setKey('id', id);
    $tactic.setKey('name', name || currentFormation?.name || 'Custom');
    $tactic.setKey('category', 'Custom');
    // Only send fields expected by the mutation input, omitting createdAt/updatedAt
    void savePlay({
      ...(saveAsNew ? { id } : { id: currentFormation?.id }),
      name: name || currentFormation?.name || 'Custom',
      snapshots: currentFormation?.snapshots || [],
      category: 'Custom',
      isDraft: saveAsNew,
      synced: false,
      licenseGroup, // Coming from the AuthContext
      namings,
    });
    resetToolbar();
  };

  // ----- Delete play logic
  const onSelectPlayForDelete = (play: any) => {
    setSelectedPlayForDelete(play);
    setDeletingPlayModalOpen(true);
  };

  const onCancelDeletePlay = () => {
    setDeletingPlayModalOpen(false);
    setSelectedPlayForDelete(null);
  };

  const asyncWait = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const onDeletePlay = useCallback(async () => {
    // console.log('onDeletePlay called with selectedPlayForDelete:', selectedPlayForDelete)
    if (!selectedPlayForDelete || !$isOnline.get()) {
      return;
    }
    try {
      setDeleteInProgress(true);
      await deletePlay(selectedPlayForDelete.id!);
      await asyncWait(1000);
      await loadPlays();
    } catch (e) {
    } finally {
      setDeleteInProgress(false);
    }
    setDeletingPlayModalOpen(false);
    resetToolbar();
    // setCurrentFormationId("pos66")
  }, [
    selectedPlayForDelete,
    setDeleteInProgress,
    resetToolbar,
    setDeletingPlayModalOpen,
    deletePlay,
  ]);

  // ----- Screen capture logic
  const captureScreen = async () => {
    if (!poolRef.current) {
      return;
    }
    const canvas = await html2canvas(poolRef.current, {
      backgroundColor: lighterBlueBackground,
    });
    const image = canvas
      //@ts-ignore
      .toDataURL('image/png')
      .replace('image/png', 'image/octet-stream');

    const element = document.createElement('a');
    const filename = `Playbook-export-${dayjs().format('YYYYMMDDHHmmss')}.png`;
    element.setAttribute('href', image);
    element.setAttribute('download', filename);

    element.click();
  };

  const onUpgrade = useCallback(() => {
    setPreviousView(activeView);
    setActiveView(ViewType.UPGRADE);
  }, [activeView, setActiveView, setPreviousView]);

  const calculatedPlays = useMemo(() => {
    return isPro
      ? folders.reduce((result, folder) => {
          return {
            ...result,
            [folder]: [...playsStore, ...positions].filter(
              (play) => play.category === folder,
            ),
          };
        }, {})
      : { basic: [positions[0], positions[1]] };
  }, [folders, positions, playsStore, isPro]);

  useEffect(() => {
    setIsPlayingFinished(false);
    setIsPlaying(false);
  }, [activeView]);

  return (
    <>
      <VersionInfo />
      <Modal
        open={isDeletingPlayModalOpen}
        centered={true}
        closable={false}
        okText="Delete"
        footer={null}
        styles={{
          header: {
            background: lighterBlueBackground,
            fontSize: '30px !important',
            color: '#fff',
          },
          content: {
            background: lighterBlueBackground,
            color: '#fff',
          },
        }}
      >
        <div
          style={{ padding: '20px', fontSize: '1.4rem', textAlign: 'center' }}
        >
          Are you sure you want to delete play:
          <div style={{ paddingTop: '4px' }}>
            <span style={{ fontWeight: 'bold' }}>
              {' '}
              {selectedPlayForDelete?.name}{' '}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '24px',
              gap: '24px',
            }}
          >
            <Button
              disabled={isDeletingPlay || deleteInProgress}
              large={true}
              style={{ margin: 0, background: red, color: '#fff' }}
              onClick={() => onDeletePlay()}
            >
              Delete
            </Button>
            <Button
              disabled={isDeletingPlay || deleteInProgress}
              large={true}
              secondary={true}
              style={{ margin: 0 }}
              onClick={() => onCancelDeletePlay()}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
      <TacticBoardContainer $colorMode={colorMode || 'light'}>
        <MainContainer>
          {activeView === ViewType.PLAY && (
            <>
              {currentFormation !== undefined && (
                <div
                  style={{
                    userSelect: 'none',
                    pointerEvents: 'none',
                    position: 'absolute',
                    top: '14px',
                    right: '20px',
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '1.2rem',
                  }}
                >
                  {currentFormation.name}
                </div>
              )}
            </>
          )}
          {(activeToolbarControl === ToolbarControl.EDITING ||
            activeToolbarControl === ToolbarControl.PLAYING) &&
          activeView === ViewType.PLAY ? (
            <>
              <div
                style={{
                  position: 'absolute',
                  top: '24px',
                  left: '14px',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  height: '26px',
                  opacity: 0.8,
                  zIndex: 100,
                  gap: '20px',
                }}
              >
                <SwitchTransitionModeButton />
                {activeToolbarControl === ToolbarControl.EDITING && (
                  <SwitchMovementModeButton />
                )}
              </div>
            </>
          ) : (
            <></>
          )}
          {activeToolbarControl === ToolbarControl.PLAYING && (
            <>
              <div
                style={{
                  position: 'absolute',
                  bottom: '20px',
                  left: '14px',
                  color: '#fff',
                  borderRadius: '12px',
                  border: '2px solid #fff',
                  width: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  height: '26px',
                  opacity: 0.8,
                }}
              >
                {activeSnapshotIndex + 1}
              </div>
            </>
          )}
          {activeView === ViewType.PLAY && (
            <Toolbar
              activeControl={activeToolbarControl}
              colorMode={colorMode}
              onControlChange={setActiveToolbarControl}
              onColorModeChange={setColorMode}
              onCaptureScreen={captureScreen}
              drawingMode={drawingMode}
              onDrawingModeChange={setDrawingMode}
              onCleanDrawings={setShouldCleanDrawings}
              onUndoLastDrawing={setShouldUndoLastDrawing}
              isInstallable={
                !isStandalone &&
                (downloadEvent !== null || isIOS || isMobileSafari)
              }
              isSubscribed={!!isPro}
              isPlaying={isPlaying}
              isPlayable={(currentFormation?.snapshots?.length || 0) > 1}
              onRotatePlay={() => {
                $isHalfView.set(!halfView);
                setHalfView(!halfView);
              }}
              onUpgrade={onUpgrade}
              onInstall={() => {
                if (downloadEvent) {
                  // @ts-ignore
                  downloadEvent?.prompt();
                }
                if (isIOS || isMobileSafari) {
                  Modal.info({
                    title: (
                      <div
                        style={{
                          fontWeight: 'bold',
                          fontFamily: '"Space Grotesk", sans-serif',
                        }}
                      >
                        Install {appName}
                      </div>
                    ),
                    content: (
                      <div
                        style={{ paddingTop: '20px', paddingBottom: '16px' }}
                      >
                        <div
                          style={{
                            textAlign: 'center',
                            fontFamily: '"Space Grotesk", sans-serif',
                          }}
                        >
                          To install {appName},
                          <br />
                          tap the Share button
                          <div style={{ textAlign: 'center' }}>
                            <img src={'/shareicon.png'} />
                          </div>
                          in Safari and select
                          <div style={{ fontWeight: 'bold' }}>
                            "Add to Home Screen".
                          </div>
                        </div>
                      </div>
                    ),
                    okText: 'Got it',
                  });
                }
              }}
              onNextSnapshot={() => {
                if (isPlayingFinished) {
                  setIsPlayingFinished(false);
                }
                goToNextFrame();
              }}
              onPrevSnapshot={() => {
                if (isPlayingFinished) {
                  setIsPlayingFinished(false);
                }
                goToPrevFrame();
              }}
              onPlayPausePlay={() => {
                isPlaying ? pausePlaying() : startPlaying();
              }}
              isReplayable={
                isPlayingFinished ||
                (activeSnapshotIndex === snapShots.length - 1 && !isPlaying)
              }
              onResetPlay={startReplaying}
              isEditing={isEditorOpen}
              onEditPlay={() => setIsEditorOpen(!isEditorOpen)}
            />
          )}
          <MainView $colorMode={colorMode || 'light'}>
            {activeView === ViewType.PLAY && !!currentFormation && (
              <PlayViewContainer ref={mainViewRef}>
                <PoolContainer>
                  <PoolElement
                    isSubscribedUser={isPro}
                    colorMode={colorMode}
                    snapshot={{ ...getCurrentSnapshot()! }}
                    namings={namings}
                    halfView={halfView}
                    poolMaxHeight={
                      isEditorOpen && MainViewRatio[activeEditor!] > 0
                        ? height * (1 - MainViewRatio[activeEditor!]) * 0.9
                        : height
                    }
                    poolMaxWidth={
                      isEditorOpen && MainViewRatio[activeEditor!] > 0
                        ? height *
                          (1 - MainViewRatio[activeEditor!]) *
                          0.9 *
                          1.6
                        : width
                    }
                    isPositionSelectorOpen={false}
                    isDrawingEnabled
                    movementMode={movementMode}
                    showSavedPaths={
                      activeToolbarControl === ToolbarControl.EDITING &&
                      movementMode === MovementMode.PATH &&
                      !isPlaying &&
                      !isTransitioning
                    }
                    drawingMode={drawingMode}
                    onStopDrawing={() => setDrawingMode(DrawingMode.NONE)}
                    onDrawingsCleaned={() => setShouldCleanDrawings(false)}
                    onLastDrawingUndone={() => setShouldUndoLastDrawing(false)}
                    shouldCleanDrawings={shouldCleanDrawings}
                    shouldUndoLastDrawing={shouldUndoLastDrawing}
                    onSnapshotUpdate={(snapshot) => {
                      // const nextCurrentFormation = { ...currentFormation }
                      updateTacticSnapshot(snapshot, activeSnapshotIndex);
                      // nextCurrentFormation!.snapshots![
                      //   activeSnapshotIndex
                      // ] = snapshot
                      //@ts-ignore
                      // setCurrentFormation(nextCurrentFormation)
                    }}
                    opponentIncrease={OPPONENT_INCREASE}
                    playName={currentFormation?.name || ''}
                    poolRef={poolRef}
                    selectedPlayer={selectedPlayer}
                    onStartNamingPlayer={(index) => {
                      setSelectedPlayer(index);
                      setActiveToolbarControl(ToolbarControl.NUMBERING);
                    }}
                    onNamingPlayer={(index) => {
                      setSelectedPlayer(index);
                    }}
                    namingEditorActive={
                      activeEditor === EditorContent.CAP_NUMBERING
                    }
                    backgroundText={
                      activeEditor === EditorContent.PLAY_EDITING
                        ? `${activeSnapshotIndex + 1}`
                        : undefined
                    }
                  />
                </PoolContainer>
                {isEditorOpen && currentFormation !== undefined && (
                  <EditorContainer
                    $height={height * MainViewRatio[activeEditor!]}
                  >
                    <Editor
                      containerHeight={height * MainViewRatio[activeEditor!]}
                      containerWidth={width}
                      play={currentFormation!}
                      numbering={namings}
                      selectedPlayer={selectedPlayer}
                      usedNumbers={usedCapNumbers}
                      activeEditor={activeEditor}
                      onSelectCapNumber={(index) => () => {
                        // console.log("onSelectCapNumber:index", index)
                        const newNamings = [...namings];
                        newNamings[selectedPlayer!] = !!index
                          ? index.toString()
                          : null;
                        updateTacticNamings(newNamings);
                        // Close the picker modal (it is gated on
                        // selectedPlayer !== null) while staying in numbering
                        // mode so another player can be numbered next.
                        setSelectedPlayer(null);
                      }}
                      isPlaying={isPlaying}
                      selectedFrame={activeSnapshotIndex}
                      onSelectFrame={onSelectFrameManually}
                      onRemoveFrame={onRemoveFrame}
                      onAddFrame={onAddNewFrame}
                      onNextFrame={goToNextFrame}
                      onPrevFrame={goToPrevFrame}
                      onStartPlay={startPlaying}
                      onStopPlay={pausePlaying}
                      onSubmitPlay={onSubmitPlay}
                      playNameList={playNameList}
                    />
                  </EditorContainer>
                )}
              </PlayViewContainer>
            )}
            {activeView === ViewType.PLAY_BROWSER && (
              <PlayBrowser
                isPro={isPro}
                colorMode={colorMode}
                isOpen={true}
                onSelectPlay={(formationId) => {
                  onFormationChange(formationId);
                  // setCurrentNote(null)
                  // setIsCreatingNote(false)
                  setActiveView(ViewType.PLAY);
                  setActiveToolbarControl(undefined);
                }}
                onAddPlay={(selectedFolder: string) => {
                  const plays = isPro
                    ? folders.reduce((result, folder) => {
                        return {
                          ...result,
                          [folder]: [...playsStore, ...positions].filter(
                            (play) => play.category === folder,
                          ),
                        };
                      }, {})
                    : { basic: [positions[0], positions[1]] };

                  // Select the first play in the list
                  let firstPlay = Object.values(plays)?.[0]?.[0];
                  if (isPro && selectedFolder !== 'Custom') {
                    firstPlay = plays[selectedFolder][0];
                  }
                  if (firstPlay && firstPlay.id) {
                    onFormationChange(firstPlay.id);
                    setActiveView(ViewType.PLAY);
                    setTimeout(() => {
                      setActiveToolbarControl(ToolbarControl.EDITING);
                    }, 100);
                  }
                }}
                poolMaxHeight={height}
                onDeletePlay={onSelectPlayForDelete}
                plays={calculatedPlays}
                // onNewNote={() => {
                //   setIsCreatingNote(true)
                //   setCurrentNote(null)
                // }}
                // onNoteSelect={note => {
                //   setCurrentNote(note)
                //   setIsCreatingNote(false)
                //   setCurrentFormationId("")
                // }}
              />
            )}
            {activeView === ViewType.SETTINGS && (
              <ProfilePage onUpgrade={onUpgrade} />
            )}
            {activeView === ViewType.ADMIN && <AdminContainer />}
            {activeView === ViewType.UPGRADE && (
              <UpgradeSubscription
                onCancel={() => {
                  setActiveView(previousView);
                }}
              />
            )}
          </MainView>
          <Menu
            activeView={activeView}
            onViewChange={(view) => {
              if (view === ViewType.PLAY) {
                resetToolbar();
                setActiveEditor(undefined);
                setIsEditorOpen(false);
              }
              setActiveView(view);
            }}
            isAdmin={isAdmin}
            isPro={isPro}
            onUpgrade={onUpgrade}
            onColorModeChange={(mode: 'light' | 'dark') => {
              setColorMode(mode);
            }}
            colorMode={colorMode || 'dark'}
          />
        </MainContainer>
      </TacticBoardContainer>
    </>
  );
};
